/**
 * Tiny formatters that keep call-site readable. Centralized so a change
 * to "how do we render an Instant?" lands in one place.
 */

const RTF = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

const UNITS: { unit: Intl.RelativeTimeFormatUnit; ms: number }[] = [
  { unit: 'year', ms: 365.25 * 24 * 60 * 60 * 1000 },
  { unit: 'month', ms: 30 * 24 * 60 * 60 * 1000 },
  { unit: 'day', ms: 24 * 60 * 60 * 1000 },
  { unit: 'hour', ms: 60 * 60 * 1000 },
  { unit: 'minute', ms: 60 * 1000 },
  { unit: 'second', ms: 1000 },
];

/** "3 minutes ago", "in 2 days", etc. Falls back to absolute on bad input. */
export function relativeTime(iso: string | number | Date | null | undefined): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const diff = t - Date.now();
  for (const { unit, ms } of UNITS) {
    if (Math.abs(diff) >= ms || unit === 'second') {
      return RTF.format(Math.round(diff / ms), unit);
    }
  }
  return new Date(t).toISOString();
}

/** "2026-04-25 14:30 UTC" — when a relative is too vague (audit drilldown). */
export function utcStamp(iso: string | number | Date | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toISOString().replace('T', ' ').replace(/\.\d+Z$/, ' UTC');
}

/** "12-3456-7890-1234" → "12-…-1234". Useful when redacted display matters. */
export function redactMiddle(value: string | null | undefined, head = 4, tail = 4): string {
  if (!value) return '—';
  if (value.length <= head + tail + 1) return value;
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
}

/** Plural-aware integer label: pluralize(2, "row") → "2 rows". */
export function pluralize(n: number, singular: string, plural?: string): string {
  return `${n.toLocaleString()} ${n === 1 ? singular : plural ?? singular + 's'}`;
}
